import Link from '@/components/compat/Link';
import { useMemo, useState } from 'react';
import { ArrowUpDown, Eye, Pencil, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { painelSurfaceClass, painelTableShellClass } from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';
import type { ChangelogProductId } from '@/lib/changelog/changelog-sources';
import { useChangelogSources } from '@/hooks/use-changelog-sources';
import { changelogVersionHref } from '@/components/painel/changelog/changelog-nav-config';
import { ChangelogProductTabs } from '@/components/painel/changelog/changelog-product';
import { ChangelogBadge, ChangelogPageShell } from '@/components/painel/changelog/changelog-ui';

type PainelChangelogVersionsPageProps = {
  product: ChangelogProductId;
};

export function PainelChangelogVersionsPage({ product }: PainelChangelogVersionsPageProps) {
  const [q, setQ] = useState('');
  const { sources, loading, error } = useChangelogSources();
  const source = sources[product];

  const rows = useMemo(() => {
    return source.entries
      .filter((r) => {
        if (!q.trim()) return true;
        const s = `${r.version} ${r.date} ${r.sections.map((x) => x.label).join(' ')}`.toLowerCase();
        return s.includes(q.toLowerCase());
      })
      .map((r) => ({
        version: r.version,
        date: r.date,
        latest: !!r.latest,
        sectionsCount: r.sections.length,
        itemsCount: r.sections.reduce((acc, s) => acc + s.items.length, 0),
      }));
  }, [source.entries, q]);

  return (
    <ChangelogPageShell className="space-y-6 pb-10">
      <ChangelogProductTabs product={product} />

      <div>
        <h1 className="font-poppins text-2xl font-bold text-white md:text-3xl">Versions</h1>
        <p className="mt-1 font-inter text-sm text-slate-400">
          Versões do changelog <strong className="font-medium text-slate-300">{source.label}</strong>
          {loading ? ' — a carregar…' : null}
        </p>
        {error ? <p className="mt-1 font-inter text-xs text-amber-300/90">{error}</p> : null}
      </div>

      <div className={cn(painelSurfaceClass, 'flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between')}>
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por versão, data ou secção…"
            className="rounded-xl border-white/10 bg-white/[0.06] py-6 pl-9 text-blue-50"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl border-white/10 bg-white/[0.04]"
            onClick={() => {
              /* ordenação fixa = ordem da fonte; botão reservado */
            }}
          >
            <ArrowUpDown className="mr-2 h-4 w-4" />
            Ordem editorial
          </Button>
          <Button asChild className="rounded-xl bg-sky-500 font-poppins font-semibold hover:bg-sky-400">
            <Link href={`/painel/changelog/${product}/versions/novo`}>
              <Plus className="mr-2 h-4 w-4" />
              Nova versão
            </Link>
          </Button>
        </div>
      </div>

      <div className={cn(painelTableShellClass, 'overflow-x-auto')}>
        <table className="w-full min-w-[720px] border-separate border-spacing-0 text-left">
          <thead>
            <tr className="border-b border-white/10 bg-white/[0.03] font-poppins text-[10px] font-bold uppercase tracking-wider text-slate-400">
              <th className="px-4 py-3">Versão</th>
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3">Secções</th>
              <th className="px-4 py-3 text-center">Itens</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="font-inter text-sm text-blue-100/90">
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-slate-400">
                  A carregar versões…
                </td>
              </tr>
            ) : null}
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-slate-400">
                  Nenhuma versão encontrada.
                </td>
              </tr>
            ) : null}
            {rows.map((r) => (
              <tr key={r.version} className="border-b border-white/[0.06] transition-colors hover:bg-white/[0.02]">
                <td className="px-4 py-3 font-poppins font-semibold text-white">v{r.version}</td>
                <td className="px-4 py-3 tabular-nums text-slate-400">{r.date}</td>
                <td className="px-4 py-3 tabular-nums text-slate-300">{r.sectionsCount}</td>
                <td className="px-4 py-3 text-center tabular-nums text-slate-300">{r.itemsCount}</td>
                <td className="px-4 py-3">
                  {r.latest ? (
                    <ChangelogBadge tone="vip">Atual</ChangelogBadge>
                  ) : (
                    <ChangelogBadge tone="live">Publicado</ChangelogBadge>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button asChild size="sm" variant="ghost" className="rounded-lg text-sky-300 hover:bg-white/10">
                      <Link href={changelogVersionHref(product, r.version)}>
                        <Eye className="mr-1.5 h-3.5 w-3.5" />
                        Ver
                      </Link>
                    </Button>
                    <Button asChild size="sm" variant="ghost" className="rounded-lg text-sky-300 hover:bg-white/10">
                      <Link href={`${changelogVersionHref(product, r.version)}/edit`}>
                        <Pencil className="mr-1.5 h-3.5 w-3.5" />
                        Editar
                      </Link>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChangelogPageShell>
  );
}
